import React, { useState, useEffect } from 'react'
import { PageLayout } from '../../components/layout/PageLayout'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { ToastContainer } from '../../components/ui/Toast'
import { SubCategoryDialog } from '../../components/categories/SubCategoryDialog'
import { useToast } from '../../hooks/useToast'
import { CategoriesService } from '../../services/api/categoriesService'
import { ProductCategory, SubCategory } from '../../types/category'
import { logger } from '../../utils/logger'
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'

export const CategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<ProductCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const { toasts, showToast, removeToast } = useToast()

  // Dialog states
  const [showAddCategoryDialog, setShowAddCategoryDialog] = useState(false)
  const [showEditCategoryDialog, setShowEditCategoryDialog] = useState(false)
  const [showSubCategoryDialog, setShowSubCategoryDialog] = useState(false)
  const [showDeleteCategoryDialog, setShowDeleteCategoryDialog] = useState(false)
  const [showDeleteSubCategoryDialog, setShowDeleteSubCategoryDialog] = useState(false)

  // Form states
  const [categoryName, setCategoryName] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<ProductCategory | null>(null)
  const [selectedSubCategory, setSelectedSubCategory] = useState<SubCategory | null>(null)
  const [categoryToDelete, setCategoryToDelete] = useState<ProductCategory | null>(null)
  const [subCategoryToDelete, setSubCategoryToDelete] = useState<{ category: ProductCategory, subCategory: SubCategory } | null>(null)

  useEffect(() => {
    fetchCategories()
  }, [])

  const fetchCategories = async () => {
    setLoading(true)
    try {
      const data = await CategoriesService.getCategories()
      logger.log('Categories data received:', data)
      // Sort categories alphabetically
      const sortedData = [...data].sort((a, b) => 
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      )
      setCategories(sortedData)
    } catch (error) {
      logger.error('Error fetching categories:', error)
      showToast('Erreur lors du chargement des catégories', 'error')
    } finally {
      setLoading(false)
    }
  }

  // Category CRUD operations
  const handleAddCategory = () => {
    setCategoryName('')
    setShowAddCategoryDialog(true)
  }

  const handleEditCategory = (category: ProductCategory) => {
    setSelectedCategory(category)
    setCategoryName(category.name)
    setShowEditCategoryDialog(true)
  }

  const handleDeleteCategory = (category: ProductCategory) => {
    setCategoryToDelete(category)
    setShowDeleteCategoryDialog(true)
  }

  const submitAddCategory = async () => {
    if (!categoryName.trim()) return

    setSaving(true)
    try {
      const newCategory = await CategoriesService.createCategory({
        name: categoryName.trim(),
        subCategories: []
      })
      
      // Add to list and sort
      const updatedCategories = [...categories, newCategory].sort((a, b) => 
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      )
      setCategories(updatedCategories)
      
      showToast('Catégorie créée avec succès', 'success')
      setShowAddCategoryDialog(false)
      setCategoryName('')
    } catch (error) {
      logger.error('Error creating category:', error)
      showToast('Erreur lors de la création de la catégorie', 'error')
    } finally {
      setSaving(false)
    }
  }

  const submitEditCategory = async () => {
    if (!selectedCategory || !categoryName.trim()) return

    setSaving(true)
    try {
      const updatedCategory = await CategoriesService.updateCategory({
        ...selectedCategory,
        name: categoryName.trim()
      })
      
      // Update in list
      setCategories(categories.map(c => 
        c.id === updatedCategory.id ? updatedCategory : c
      ))
      
      showToast('Catégorie modifiée avec succès', 'success')
      setShowEditCategoryDialog(false)
      setSelectedCategory(null)
      setCategoryName('')
    } catch (error) {
      logger.error('Error updating category:', error)
      showToast('Erreur lors de la modification de la catégorie', 'error')
    } finally {
      setSaving(false)
    }
  }

  const confirmDeleteCategory = async () => {
    if (!categoryToDelete?.id) return

    setSaving(true)
    try {
      await CategoriesService.deleteCategory(categoryToDelete.id)
      setCategories(categories.filter(c => c.id !== categoryToDelete.id))
      showToast('Catégorie supprimée avec succès', 'success')
      setShowDeleteCategoryDialog(false)
      setCategoryToDelete(null)
    } catch (error) {
      logger.error('Error deleting category:', error)
      showToast(
        'Erreur lors de la suppression de la catégorie. Il est possible que des produits soient associés à cette catégorie.',
        'error'
      )
    } finally {
      setSaving(false)
    }
  }

  // SubCategory CRUD operations
  const handleAddSubCategory = (category: ProductCategory) => {
    setSelectedCategory(category)
    setSelectedSubCategory(null)
    setShowSubCategoryDialog(true)
  }

  const handleEditSubCategory = (category: ProductCategory, subCategory: SubCategory) => {
    setSelectedCategory(category)
    setSelectedSubCategory(subCategory)
    setShowSubCategoryDialog(true)
  }

  const handleDeleteSubCategory = (category: ProductCategory, subCategory: SubCategory) => {
    setSubCategoryToDelete({ category, subCategory })
    setShowDeleteSubCategoryDialog(true)
  }

  const submitSubCategory = async (subCategory: SubCategory) => {
    if (!selectedCategory?.id) return

    try {
      const galenicFormIds = subCategory.galenicForms.map(f => f.id!).filter(id => id !== undefined)

      if (selectedSubCategory?.id) {
        // Update existing subcategory
        await CategoriesService.updateSubcategory(
          selectedCategory.id,
          selectedSubCategory.id,
          subCategory.name,
          galenicFormIds
        )
        showToast('Sous-catégorie modifiée avec succès', 'success')
      } else {
        // Create new subcategory
        await CategoriesService.createSubcategory(
          selectedCategory.id,
          subCategory.name,
          galenicFormIds
        )
        showToast('Sous-catégorie créée avec succès', 'success')
      }

      // Refresh categories
      await fetchCategories()
      setShowSubCategoryDialog(false)
      setSelectedCategory(null)
      setSelectedSubCategory(null)
    } catch (error) {
      logger.error('Error saving subcategory:', error)
      showToast('Erreur lors de l\'enregistrement de la sous-catégorie', 'error')
      throw error
    }
  }

  const confirmDeleteSubCategory = async () => {
    if (!subCategoryToDelete?.category.id || !subCategoryToDelete?.subCategory.id) return

    setSaving(true)
    try {
      await CategoriesService.deleteSubcategory(
        subCategoryToDelete.category.id,
        subCategoryToDelete.subCategory.id
      )
      
      // Update local state
      setCategories(categories.map(c => {
        if (c.id === subCategoryToDelete.category.id) {
          return {
            ...c,
            subCategories: c.subCategories.filter(sc => sc.id !== subCategoryToDelete.subCategory.id)
          }
        }
        return c
      }))
      
      showToast('Sous-catégorie supprimée avec succès', 'success')
      setShowDeleteSubCategoryDialog(false)
      setSubCategoryToDelete(null)
    } catch (error) {
      logger.error('Error deleting subcategory:', error)
      showToast(
        'Erreur lors de la suppression de la sous-catégorie. Il est possible que des produits soient associés à cette sous-catégorie.',
        'error'
      )
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout maxWidth="4xl">
      <ToastContainer toasts={toasts} onClose={removeToast} />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">Catégories</h1>
        <div className="flex gap-3">
          <Button
            onClick={fetchCategories}
            variant="outline"
            className="flex items-center gap-2"
          >
            <ArrowPathIcon className="h-4 w-4" />
            Rafraîchir
          </Button>
          <Button
            onClick={handleAddCategory}
            variant="primary"
            className="flex items-center gap-2"
          >
            <PlusIcon className="h-4 w-4" />
            Nouvelle catégorie
          </Button>
        </div>
      </div>

      {/* Categories List */}
      {categories.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-gray-600 dark:text-gray-400">Aucune catégorie</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {categories.map((category) => (
            <Card key={category.id} className="p-6">
              {/* Category Header */}
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-medium text-gray-900 dark:text-white">
                  {category.name}
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditCategory(category)}
                    className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteCategory(category)}
                    className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Subcategories Section */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-medium text-gray-700 dark:text-gray-300">
                    Sous-catégories
                  </h3>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => handleAddSubCategory(category)}
                    className="flex items-center gap-1"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Nouvelle sous-catégorie
                  </Button>
                </div>

                {category.subCategories.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                    Aucune sous-catégorie
                  </p>
                ) : (
                  <div className="space-y-3">
                    {category.subCategories.map((subCategory) => (
                      <div
                        key={subCategory.id}
                        className="flex items-start justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                      >
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                            {subCategory.name}
                          </h4>
                          <div className="flex flex-wrap gap-1.5">
                            {subCategory.galenicForms && subCategory.galenicForms.length > 0 ? (
                              subCategory.galenicForms.map((form) => (
                                <span
                                  key={form.id}
                                  className="px-2.5 py-1 text-xs bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full border border-gray-200 dark:border-gray-600"
                                >
                                  {form.displayName || form.name || 'Sans nom'}
                                </span>
                              ))
                            ) : (
                              <span className="text-xs text-gray-500 dark:text-gray-400 italic">
                                Aucune forme galénique
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1 ml-4">
                          <button
                            onClick={() => handleEditSubCategory(category, subCategory)}
                            className="p-1.5 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteSubCategory(category, subCategory)}
                            className="p-1.5 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add Category Dialog */}
      {showAddCategoryDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-white">
              Nouvelle catégorie
            </h3>
            <div className="mb-4">
              <label htmlFor="add-category-name" className="block text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
                Nom de la catégorie <span className="text-red-500">*</span>
              </label>
              <Input
                id="add-category-name"
                type="text"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                placeholder="Entrez le nom de la catégorie"
                className="w-full"
              />
            </div>
            <div className="flex justify-end gap-4">
              <Button
                variant="outline"
                onClick={() => setShowAddCategoryDialog(false)}
                disabled={saving}
              >
                Annuler
              </Button>
              <Button
                variant="primary"
                onClick={submitAddCategory}
                disabled={saving || !categoryName.trim()}
              >
                {saving ? 'Création...' : 'Ajouter'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Category Dialog */}
      {showEditCategoryDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-white">
              Modifier la catégorie
            </h3>
            <div className="mb-4">
              <label htmlFor="edit-category-name" className="block text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
                Nom de la catégorie <span className="text-red-500">*</span>
              </label>
              <Input
                id="edit-category-name"
                type="text"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                placeholder="Entrez le nom de la catégorie"
                className="w-full"
              />
            </div>
            <div className="flex justify-end gap-4">
              <Button
                variant="outline"
                onClick={() => setShowEditCategoryDialog(false)}
                disabled={saving}
              >
                Annuler
              </Button>
              <Button
                variant="primary"
                onClick={submitEditCategory}
                disabled={saving || !categoryName.trim()}
              >
                {saving ? 'Modification...' : 'Modifier'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* SubCategory Dialog */}
      {selectedCategory && (
        <SubCategoryDialog
          isOpen={showSubCategoryDialog}
          onClose={() => {
            setShowSubCategoryDialog(false)
            setSelectedCategory(null)
            setSelectedSubCategory(null)
          }}
          onSubmit={submitSubCategory}
          title={selectedSubCategory ? 'Modifier la sous-catégorie' : 'Nouvelle sous-catégorie'}
          categoryName={selectedCategory.name}
          existingSubCategory={selectedSubCategory || undefined}
        />
      )}

      {/* Delete Category Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteCategoryDialog}
        onClose={() => {
          setShowDeleteCategoryDialog(false)
          setCategoryToDelete(null)
        }}
        onConfirm={confirmDeleteCategory}
        title="Confirmer la suppression"
        message={`Êtes-vous sûr de vouloir supprimer la catégorie "${categoryToDelete?.name}" ? Cette action supprimera également toutes ses sous-catégories.`}
        confirmText="Supprimer"
        cancelText="Annuler"
      />

      {/* Delete SubCategory Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteSubCategoryDialog}
        onClose={() => {
          setShowDeleteSubCategoryDialog(false)
          setSubCategoryToDelete(null)
        }}
        onConfirm={confirmDeleteSubCategory}
        title="Confirmer la suppression"
        message={`Êtes-vous sûr de vouloir supprimer la sous-catégorie "${subCategoryToDelete?.subCategory.name}" ?`}
        confirmText="Supprimer"
        cancelText="Annuler"
      />
    </PageLayout>
  )
}